import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

const SLIDERS = [
    {
        id: "shopify",
        label: "Shopify",
        interval: 3200,
        slides: [
            { logo: "shopify", title: "Store Design & Development", desc: "Custom themes and storefronts built for conversion." },
            { logo: "shopify", title: "Speed Optimisation", desc: "Liquid tuning, Core Web Vitals and app-bloat cleanup." },
            { logo: "shopify", title: "Custom Apps & Integrations", desc: "Private apps, APIs and third-party connections." },
        ],
    },
    {
        id: "web",
        label: "React · Node.js · Next.js",
        interval: 4100,
        slides: [
            { logo: "react", title: "React Development", desc: "Fast, interactive interfaces and dashboards." },
            { logo: "nodedotjs", title: "Node.js Backends", desc: "APIs and services engineered to scale." },
            { logo: "nextdotjs", title: "Next.js Applications", desc: "Server-rendered, SEO-ready web apps." },
        ],
    },
];

const SliderCard = ({ slider, delay }) => {
    const [index, setIndex] = useState(0);

    useEffect(() => {
        const t = setInterval(() => setIndex((v) => (v + 1) % slider.slides.length), slider.interval);
        return () => clearInterval(t);
    }, [slider]);

    const slide = slider.slides[index];

    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay }}
            data-testid={`hero-slider-${slider.id}`}
            className="rounded-2xl border border-slate-200 bg-white/85 p-5 shadow-card backdrop-blur-md"
        >
            <div className="flex items-center justify-between gap-3">
                <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-brand">
                    {slider.label}
                </span>
                <span className="flex items-center gap-1.5">
                    {slider.slides.map((_, d) => (
                        <button
                            key={d}
                            type="button"
                            onClick={() => setIndex(d)}
                            data-testid={`hero-slider-${slider.id}-dot-${d}`}
                            aria-label={`Show slide ${d + 1}`}
                            className={`h-1.5 rounded-full transition-[width,background-color] duration-500 ${
                                d === index ? "w-4 bg-brand" : "w-1.5 bg-slate-300 hover:bg-slate-400"
                            }`}
                        />
                    ))}
                </span>
            </div>
            <div className="relative mt-4 h-[70px] overflow-hidden">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={index}
                        initial={{ x: 32, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: -32, opacity: 0 }}
                        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                        className="flex h-full items-center gap-4"
                    >
                        <img
                            src={`https://cdn.simpleicons.org/${slide.logo}`}
                            alt=""
                            loading="lazy"
                            className="h-9 w-9 shrink-0"
                        />
                        <div className="min-w-0">
                            <p className="truncate font-display text-base font-semibold tracking-tight text-slate-900">
                                {slide.title}
                            </p>
                            <p className="mt-0.5 text-xs leading-relaxed text-slate-500 sm:text-sm">
                                {slide.desc}
                            </p>
                        </div>
                    </motion.div>
                </AnimatePresence>
            </div>
        </motion.div>
    );
};

export default function HeroSliders() {
    return (
        <div className="mt-12 grid max-w-3xl grid-cols-1 gap-4 sm:grid-cols-2">
            {SLIDERS.map((s, i) => (
                <SliderCard key={s.id} slider={s} delay={1.05 + i * 0.12} />
            ))}
        </div>
    );
}
